<script setup>
// Botão "Recarregar" + modal NOSSO (sem prompt do navegador) pra digitar o valor
// em R$ e gerar a recarga via ASAAS/PIX (abre a página de pagamento).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import creditsAPI from 'dashboard/api/credits';

const props = defineProps({
  label: { type: String, default: 'Recarregar' },
  // Conta sem PIX/boleto (cliente de fora) paga no cartão.
  isCard: { type: Boolean, default: false },
});
const { isCard } = props;

const { t } = useI18n();

const open = ref(false);
const loading = ref(false);
const amount = ref(50);
const QUICK = [50, 100, 200, 500];

const brl = v =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    v || 0
  );

const submit = async () => {
  const value = Number(amount.value);
  if (!value || value < 5) {
    useAlert(t('BILLING.RECHARGE.MIN'));
    return;
  }
  loading.value = true;
  try {
    const { data } = await creditsAPI.recharge(value);
    // Conta sem PIX/boleto (cliente de fora) recebe um checkout de cartão.
    if (data.checkout_url) window.location.href = data.checkout_url;
    else if (data.invoice_url) window.open(data.invoice_url, '_blank');
    else useAlert(t('BILLING.RECHARGE.CREATED'));
    open.value = false;
  } catch {
    useAlert(t('BILLING.RECHARGE.ERROR'));
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div>
    <button
      class="rounded-lg bg-n-brand text-white py-2.5 px-4 text-sm font-semibold hover:opacity-90 w-full"
      @click="open = true"
    >
      {{ label }}
    </button>

    <!-- Modal -->
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="open = false"
    >
      <div
        class="w-full max-w-sm rounded-2xl bg-n-solid-2 border border-n-weak p-6"
      >
        <div class="flex items-center justify-between mb-1">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('BILLING.RECHARGE.TITLE') }}
          </h3>
          <button class="i-lucide-x text-n-slate-10" @click="open = false" />
        </div>
        <p class="text-xs text-n-slate-10 mb-4">
          {{ t(`BILLING.RECHARGE.${isCard ? 'HINT_CARD' : 'HINT_PIX'}`) }}
        </p>

        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ t('BILLING.RECHARGE.AMOUNT') }}
        </label>
        <input
          v-model.number="amount"
          type="number"
          min="5"
          step="1"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-lg font-bold text-n-slate-12"
        />

        <div class="grid grid-cols-4 gap-2 mt-3">
          <button
            v-for="q in QUICK"
            :key="q"
            type="button"
            class="rounded-lg border px-2 py-1.5 text-xs"
            :class="
              amount === q
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
            "
            @click="amount = q"
          >
            {{ brl(q) }}
          </button>
        </div>

        <button
          class="mt-5 w-full rounded-lg bg-n-brand text-white py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          :disabled="loading"
          @click="submit"
        >
          {{
            loading
              ? t('BILLING.RECHARGE.GENERATING')
              : t('BILLING.RECHARGE.SUBMIT', { amount: brl(amount) })
          }}
        </button>
      </div>
    </div>
  </div>
</template>
